import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiAlertCircle, FiX } from 'react-icons/fi';
import { requestEmailVerification } from '../services/api';
import { ACTION_FAILED_EVENT } from '../utils/actionError';
import '../styles/ActionToast.css';

/*
 * ActionToast — the one place a failed click is announced, app-wide.
 * Callers don't render anything: they call notifyActionFailed(action,
 * err) (utils/actionError.js) and this listens for it. Mounted once in
 * App.js OUTSIDE <AnimatePresence>: PageTransition's transform would
 * otherwise re-anchor `position: fixed` to the page and push the toast
 * off-screen (same root cause as the old invisible bracket-toast).
 *
 * Top-center rather than bottom: MatchupPage / BracketPage pin a
 * "Join community" bar to the bottom, and a failed join must not hide
 * behind the very button that failed.
 */
const VISIBLE_MS = 6000;

const ActionToast = () => {
  const [toast, setToast] = useState(null);
  const [resend, setResend] = useState('idle'); // idle | sending | sent | error
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const dismiss = useCallback(() => {
    clearTimer();
    setToast(null);
  }, []);

  const armTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setToast(null), VISIBLE_MS);
  }, []);

  useEffect(() => {
    const onFailed = (event) => {
      if (!event?.detail) return;
      // A newer failure replaces the older one — the latest click is
      // the one the user is looking at.
      setToast({ ...event.detail, key: Date.now() });
      setResend('idle');
      armTimer();
    };
    window.addEventListener(ACTION_FAILED_EVENT, onFailed);
    return () => {
      window.removeEventListener(ACTION_FAILED_EVENT, onFailed);
      clearTimer();
    };
  }, [armTimer]);

  const handleResend = async () => {
    if (resend === 'sending' || resend === 'sent') return;
    setResend('sending');
    clearTimer(); // don't vanish mid-request
    try {
      await requestEmailVerification();
      setResend('sent');
    } catch (err) {
      setResend('error');
    } finally {
      armTimer();
    }
  };

  if (!toast) return null;

  return (
    <div
      key={toast.key}
      className="action-toast"
      role="alert"
      aria-live="assertive"
      // Hovering (or focusing) holds the toast so it can be read; leaving
      // restarts the clock.
      onMouseEnter={clearTimer}
      onMouseLeave={armTimer}
      onFocus={clearTimer}
      onBlur={armTimer}
    >
      <FiAlertCircle className="action-toast__icon" aria-hidden="true" />
      <div className="action-toast__body">
        <div className="action-toast__title">{toast.title}</div>
        <div className="action-toast__detail">
          {resend === 'sent'
            ? 'Sent — check your inbox for the link.'
            : resend === 'error'
              ? 'Couldn’t send right now. Try again from Account Settings.'
              : toast.detail}
        </div>
        {toast.verifyGate && resend !== 'sent' && (
          <button
            type="button"
            className="action-toast__action"
            onClick={handleResend}
            disabled={resend === 'sending'}
          >
            {resend === 'sending' ? 'Sending…' : 'Resend link'}
          </button>
        )}
      </div>
      <button
        type="button"
        className="action-toast__close"
        aria-label="Dismiss"
        onClick={dismiss}
      >
        <FiX aria-hidden="true" />
      </button>
    </div>
  );
};

export default ActionToast;
